import { createContext, useEffect, useMemo, useRef, useState } from "react";
import {
	currentScript,
	getParam,
	getStore,
	loadAffiliations,
	loadAppConfiguration,
	loadBrandParam,
	loadLocaleParam,
	validateParam,
	validateParams,
	validateParamValue,
	validateMessageCenterParam,
} from "utils/paramsValidation";
import {
	loadExternalThemeStyles,
	loadThemeConfigurations,
	preloadThemeCss,
} from "utils/theme";
import useProfile from "hooks/useProfile";
import { getDefaultLocale, validateGdprDomainByBrand } from "utils/language";
import { getOperatingSystem } from "utils/clientDetails";
import appConfig from "config.json";
import { getCdn, getEnvironment } from "utils";
import { dynamicallyLoadScript, validateScript } from "utils/externalScripts";
import useRouteChange from "hooks/useRouteChange";
import { getLogo, preloadLogoUrl } from "utils/logo";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
import { getNewChatParams } from "utils/newChat";

export const Context = createContext();

const cdnAssetsUrl = getCdn(true);
const cdnUrl = getCdn();
const { defaultPosition, brands, externalScripts, mobileMediaQuery } =
	appConfig;

const timeToReloadParameters = 100;
const contextEvent = "navigationContext";
let runtimeConfiguration = {};

const dispatchContextReadyEvent = () => {
	document?.dispatchEvent(
		new CustomEvent(`${contextEvent}Listening`, {
			detail: {
				status: "ready",
			},
		})
	);
};

const ContextProvider = (props) => {
	const brand = useMemo(() => loadBrandParam(), []);
	const ariaLiveRef = useRef();
	const [config, setConfig] = useState({
		absoluteLinksFromBrand: false,
		affiliations: [],
		allowedLocales: "",
		banner: null,
		bannerProfile: false,
		basePath: "",
		brand,
		chrome: false,
		chromeLinks: true,
		environment: getEnvironment(),
		footer: false,
		header: false,
		hideFullNavigation: false,
		hideLegalFooterLinks: false,
		hideOnPrint: true,
		languageSelector: true,
		legal: false,
		locale: loadLocaleParam(),
		logo: true,
		logoRedirect: true,
		messageBanner: true,
		messageCenter: true,
		navigation: false,
		oneIdOnly: false,
		operatingSystem: getOperatingSystem(),
		overrideLogo: "",
		position: defaultPosition,
		profile: true,
		searchBar: true,
		showBackButton: false,
		showDisney: true,
		showRelatedDisneySites: true,
		showSections: true,
		showSignInSignOut: true,
		skipOneId: false,
		smartBanner: true,
		store: getStore(),
		theme: "",
		travelAgentHeader: true,
		openFooterLinksInNewTab: false,
		additionalProfileInfo: false,
		isNewChatEnabled: false,
		logoUrl: getLogo(brand, brands, false, cdnAssetsUrl),
	});
	const [headerData, setHeaderData] = useState({ loading: true });
	const [footerData, setFooterData] = useState({ loading: true });
	const [legalFooterData, setLegalFooterData] = useState({ loading: true });
	const [cartHasItems, setCartHasItems] = useState(false);
	const [cartInformation, setCartInformation] = useState(null);
	const [updatedLocation, setUpdatedLocation] = useState(null);
	const [chat, setChat] = useState({
		...getNewChatParams(),
		chatWindow: null,
		chatModal: null,
		chatFloatingButton: null,
		analyticsEvent: null,
		isConversationStarted: false,
		closeChat: null,
	});
	const profileData = useProfile(
		config.brand,
		config.locale,
		config.profile,
		config.store,
		config.affiliations,
		headerData?.chrome,
		config.basePath,
		config.additionalProfileInfo,
		config.bannerProfile,
		config.disableProfileLibrary,
		chat.isConversationStarted,
		chat.closeChat,
		cdnUrl
	);
	const [modal, setModal] = useState(null);
	const isMobileView = useCheckMediaQuery(mobileMediaQuery);
	const loadConfiguration = () => {
		const overrideLogo = getParam("overrideLogo");
		setConfig((currentConfig) => ({
			...currentConfig,
			absoluteLinksFromBrand: validateParam("absoluteLinksFromBrand"),
			allowedLocales: getParam("allowedLocales"),
			chrome:
				(validateParam("chrome") ||
					(validateParam("header") && !validateParam("hideNavigation"))) &&
				!getStore(),
			chromeLinks: validateParam("chromeLinks", true),
			footer: validateParam("footer"),
			header: validateParams(["header", "chrome", "navigation"]),
			hideFullNavigation: validateParam("hideFullNavigation"),
			hideLegalFooterLinks: validateParam("hideLegalFooterLinks"),
			hideOnPrint: validateParam("hideOnPrint", true),
			languageSelector: validateParam("languageSelector", true),
			legal: validateParam("legal"),
			messageCenter: validateMessageCenterParam(
				"messageCenter",
				config.brand,
				config.locale
			),
			logoRedirect: validateParam("logoRedirect", true),
			navigation:
				validateParams(["header", "navigation"]) &&
				!validateParam("hideNavigation"),
			oneIdOnly: validateParam("oneIdOnly", false),
			overrideLogo,
			position: validateParamValue("position", ["top", "sticky"]),
			profile: validateParam("profile", true),
			searchBar:
				validateParam("searchBar", true) && !validateParam("hideNavigation"),
			messageBanner: validateParam("messageBanner", true),
			showBackButton: validateParam("showBackButton", false),
			showRelatedDisneySites: validateParam("showRelatedDisneySites", true),
			showSections: validateParam("showSections", true),
			showSignInSignOut: validateParam("showSignInSignOut", true),
			skipOneId: validateParam("skipOneId", false),
			smartBanner: validateParam("smartBanner", true),
			store: getStore(),
			travelAgentHeader: validateParam("travelAgentHeader", true),
			affiliations: loadAffiliations(),
			showDisney: validateParam("showDisney", true),
			logo: validateParam("logo", true),
			theme: getParam("theme"),
			openFooterLinksInNewTab: validateParam("openFooterLinksInNewTab"),
			additionalProfileInfo: validateParam("additionalProfileInfo", false),
			defaultLocale: getDefaultLocale(config.brand),
			disableProfileLibrary: validateParam("disableProfileLibrary", false),
			logoUrl: getLogo(brand, brands, overrideLogo, cdnAssetsUrl),
			...runtimeConfiguration,
		}));
		loadThemeConfigurations(setNewConfig, cdnUrl, cdnAssetsUrl);
	};

	const setNewConfig = (key, value) => {
		setConfig((currentConfig) => ({ ...currentConfig, [key]: value }));
	};

	const getBasePath = (
		currentLocale,
		defaultLocale,
		brand,
		absoluteLinksFromBrand
	) => {
		let baseDomain = "";
		if (absoluteLinksFromBrand && brands?.[brand]?.domains) {
			baseDomain =
				brands[brand].domains[currentLocale] ||
				brands[brand].domains.default ||
				"";
		}

		if (brands?.[brand]?.shouldLocalizeUrls === false) {
			return baseDomain;
		}
		return (
			baseDomain + (currentLocale === defaultLocale ? "" : `/${currentLocale}`)
		);
	};

	const reloadAppConfiguration = () => {
		loadAppConfiguration();
		loadConfiguration();
	};

	const onRouteChange = (newPath) => {
		setUpdatedLocation(newPath);
	};

	useRouteChange(onRouteChange);

	useEffect(() => {
		if (!config.brand || !config.environment || !config.locale) return;
		Object.keys(externalScripts).forEach((script) => {
			const url = validateScript({
				script,
				currentBrand: config.brand,
				currentEnvironment: config.environment,
				currentLocale: config.locale,
				currentStore: config.store,
			});
			url && dynamicallyLoadScript(url, cdnAssetsUrl);
		});
	}, [config.brand, config.environment, config.locale, config.store]);

	useEffect(() => {
		if (validateGdprDomainByBrand(config.brand, config.locale)) return;
		if (config.locale) {
			const defaultLocale = getDefaultLocale(config.brand);
			const basePath = getBasePath(
				config.locale,
				defaultLocale,
				config.brand,
				config.absoluteLinksFromBrand
			);
			setConfig((config) => ({ ...config, basePath }));
		}
	}, [config.locale, config.brand, config.absoluteLinksFromBrand]);

	useEffect(() => {
		setConfig((currentConfig) => ({
			...currentConfig,
			...(legalFooterData?.context || {}),
			...(footerData?.context || {}),
			...(headerData?.context || {}),
		}));
		loadExternalThemeStyles(
			headerData?.theme || footerData?.theme || legalFooterData?.theme,
			cdnUrl,
			cdnAssetsUrl
		);
	}, [headerData, footerData, legalFooterData]);

	useEffect(() => {
		preloadLogoUrl(config.logoUrl);
	}, [config.logoUrl]);

	useEffect(() => {
		const preloadTheme = brands[config.brand]?.preloadTheme;
		if (preloadTheme) {
			preloadThemeCss(preloadTheme, cdnUrl, cdnAssetsUrl);
		}
	}, [config.brand]);

	useEffect(() => {
		const contextEventLogic = (event) => {
			runtimeConfiguration = {
				...runtimeConfiguration,
				...(event?.detail || {}),
			};
			setConfig((config) => {
				const newConfig = { ...config, ...(event?.detail || {}) };
				loadExternalThemeStyles(event?.detail?.theme, cdnUrl, cdnAssetsUrl);
				return newConfig;
			});
		};
		document?.addEventListener(contextEvent, contextEventLogic);
		dispatchContextReadyEvent();
		loadConfiguration();
		let observer;
		if (currentScript) {
			observer = new MutationObserver((changes) => {
				changes.forEach((change) => {
					if (change.attributeName.includes("src")) {
						reloadAppConfiguration();
					}
				});
			});
			observer.observe(currentScript, { attributes: true });
			setTimeout(() => {
				reloadAppConfiguration();
			}, timeToReloadParameters);
		}
		return () => {
			document?.removeEventListener(contextEvent, contextEventLogic);
			if (observer) {
				observer.disconnect?.();
			}
		};
		// We intentionally run this effect only once on mount to set up observers and configuration reload.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Context.Provider
			value={{
				config,
				profileData,
				headerData,
				setHeaderData,
				footerData,
				setFooterData,
				legalFooterData,
				setLegalFooterData,
				modal,
				setModal,
				setConfig,
				cartHasItems,
				setCartHasItems,
				cartInformation,
				setCartInformation,
				updatedLocation,
				setUpdatedLocation,
				ariaLiveRef,
				chat,
				setChat,
				isMobileView,
				cdnUrl,
				cdnAssetsUrl,
			}}
		>
			{props.children}
		</Context.Provider>
	);
};

export default ContextProvider;
